import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';
import { useNavbar } from '@/contexts/NavbarContext';

function LangSwitcher({ whiteNav, compact }: { whiteNav: boolean; compact?: boolean }) {
  const { i18n } = useTranslation();
  const currentLanguage = (i18n.language || i18n.resolvedLanguage || 'tr').toLowerCase();
  const enActive = currentLanguage.startsWith('en');
  const trActive = !enActive;
  const switchLanguage = (lang: 'tr' | 'en') => {
    if (currentLanguage.startsWith(lang)) return;
    void i18n.changeLanguage(lang);
  };

  const shell = whiteNav
    ? { border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.10)' }
    : { border: '1px solid var(--border2)', background: 'var(--surface)' };

  const segment = (active: boolean) =>
    whiteNav
      ? {
          color: active ? 'var(--orange)' : 'rgba(255,255,255,0.88)',
          background: active ? 'rgba(232,97,26,0.22)' : 'transparent',
        }
      : {
          color: active ? 'var(--orange)' : 'var(--text2)',
          background: active ? 'rgba(232,97,26,0.18)' : 'transparent',
        };

  const pad = compact ? '6px 10px' : '8px 12px';
  const fs = compact ? 11 : 12;

  return (
    <div
      data-i18n-skip="true"
      className="relative z-[60] flex items-stretch rounded-full overflow-hidden flex-shrink-0 pointer-events-auto"
      style={{ ...shell, height: compact ? 36 : 38 }}
      title="Dil / Language"
    >
      <button
        type="button"
        onClick={() => switchLanguage('tr')}
        className="relative z-[70] pointer-events-auto cursor-pointer font-semibold transition-all duration-150 border-0 active:scale-95"
        style={{ padding: pad, fontSize: fs, ...segment(trActive) }}
      >
        TR
      </button>
      <div
        className="w-px flex-shrink-0 self-stretch my-1.5"
        style={{ background: whiteNav ? 'rgba(255,255,255,0.2)' : 'var(--border)' }}
        aria-hidden
      />
      <button
        type="button"
        onClick={() => switchLanguage('en')}
        className="relative z-[70] pointer-events-auto cursor-pointer font-semibold transition-all duration-150 border-0 active:scale-95"
        style={{ padding: pad, fontSize: fs, ...segment(enActive) }}
      >
        EN
      </button>
    </div>
  );
}

const LOGO = 'https://storage.readdy-site.link/project_files/0f27b860-7899-4d17-a451-e043696e780c/51ae8833-6174-4697-a4e4-f9113fa91181_cargonet_PNG.png?v=3ebb7523aaf49333dc87e7813a9a53fc';

const serviceLinks = [
  { label: 'Ekspres Kurye Servisi', icon: 'ri-flight-takeoff-line', path: '/hizmetler/ekspres-kurye-servisi' },
  { label: 'Uluslararası E-Ticaret Servisi', icon: 'ri-store-2-line', path: '/hizmetler/uluslararasi-e-ticaret-servisi' },
  { label: 'Uluslararası Kargo Taşımacılığı', icon: 'ri-ship-line', path: '/hizmetler/uluslararasi-kargo-tasimaciligi' },
  { label: 'Fulfillment', icon: 'ri-archive-2-line', path: '/hizmetler/fulfillment' },
  { label: 'Online Mağaza', icon: 'ri-shopping-bag-line', path: '/hizmetler/online-magaza' },
  { label: 'Mikro İhracat – ETGB', icon: 'ri-global-line', path: '/hizmetler/mikro-ihracat-etgb' },
  { label: 'A.TR Belgesi', icon: 'ri-file-text-line', path: '/hizmetler/atr-belgesi' },
  { label: 'On Board Kurye Servisi', icon: 'ri-user-star-line', path: '/hizmetler/on-board-kurye-servisi' },
  { label: 'Sigorta', icon: 'ri-shield-check-line', path: '/hizmetler/sigorta' },
];

const links = [
  { label: 'Ana Sayfa', path: '/' },
  { label: 'Hakkımızda', path: '/hakkimizda' },
  { label: 'Hizmetler', path: '/hizmetler', hasDropdown: true },
  { label: 'Filo & Teknoloji', path: '/filo-ve-teknoloji' },
  { label: 'Referanslar', path: '/referanslar' },
  { label: 'SSS', path: '/sss' },
  { label: 'İletişim', path: '/iletisim' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { darkHero } = useNavbar();
  // Show white text when: not scrolled AND (page has dark hero OR site is in dark mode)
  const whiteNav = !scrolled && (darkHero || isDark);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => { setOpen(false); setDropdownOpen(false); setMobileServicesOpen(false); }, [loc.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const active = (p: string) => loc.pathname === p;

  const hotlineColor = whiteNav ? 'rgba(255,255,255,0.96)' : 'var(--text1)';

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[300] transition-all duration-500"
      style={{
        background: scrolled ? 'var(--navbar-scrolled)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid var(--border)' : 'none',
      }}
    >
      {/* Top bar — phone */}
      <div
        className="hidden lg:flex items-center justify-end px-6 lg:px-10 xl:px-14 py-2"
        style={{
          borderBottom: `1px solid ${whiteNav ? 'rgba(255,255,255,0.12)' : 'var(--border)'}`,
          background: whiteNav ? 'rgba(0,0,0,0.15)' : 'transparent',
          backdropFilter: whiteNav ? 'blur(4px)' : 'none',
        }}
      >
        <a
          href="tel:+908502420822"
          rel="nofollow"
          className="flex items-center gap-2.5 text-[15px] lg:text-[16px] cursor-pointer transition-colors duration-300 whitespace-nowrap"
          style={{ color: hotlineColor, fontWeight: 600 }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--orange)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = hotlineColor; }}
        >
          <i className="ri-phone-line text-lg flex-shrink-0" style={{ color: 'var(--orange)' }} aria-hidden />
          <span className="font-semibold">Müşteri Hizmetleri</span>
          <span className="font-medium opacity-80" aria-hidden>
            ·
          </span>
          <span className="font-bold tracking-wide">0850 242 0 822</span>
        </a>
      </div>

      {/* Main nav row */}
      <div className="w-full mx-auto px-4 lg:px-10 xl:px-14">
        <div className="flex items-center justify-between min-h-[92px] h-[92px] lg:min-h-[108px] lg:h-[108px] gap-2 lg:gap-3 min-w-0">

          {/* Logo — satır yüksekliği içinde kalır; transform scale yok (üst/alt taşma yok) */}
          <Link
            to="/"
            className="relative z-10 flex h-full max-h-full flex-shrink-0 cursor-pointer items-center overflow-hidden min-w-0 self-stretch pr-2 lg:pr-4"
          >
            <img
              src={LOGO}
              alt="CargoNet"
              className="block h-auto w-auto max-h-[66px] max-w-[280px] object-contain object-left transition-all duration-400 lg:max-h-[88px] lg:max-w-[360px]"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="relative z-20 hidden lg:flex items-center gap-0.5 min-w-0 flex-1 justify-center pointer-events-auto overflow-visible">
            {links.map((l) => (
              l.hasDropdown ? (
                <div key={l.path} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen((v) => !v)}
                    className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[14px] transition-all duration-250 whitespace-nowrap cursor-pointer"
                    style={{
                      fontWeight: active(l.path) ? 700 : 500,
                      background: active(l.path) || dropdownOpen ? 'rgba(232,97,26,0.18)' : 'transparent',
                      color: active(l.path) || dropdownOpen
                        ? 'var(--orange)'
                        : whiteNav ? 'rgba(255,255,255,0.90)' : 'var(--text2)',
                    }}
                  >
                    {l.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {dropdownOpen && (
                    <div
                      className="absolute top-full left-1/2 mt-2 rounded-2xl overflow-hidden z-[400]"
                      style={{
                        transform: 'translateX(-50%)',
                        width: 300,
                        background: 'var(--panel-bg)',
                        border: '1px solid var(--border2)',
                        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                      }}
                    >
                      <div className="p-2">
                        {serviceLinks.map((svc, i) => (
                          <Link
                            key={i}
                            to={svc.path}
                            className="flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 cursor-pointer"
                            style={{ textDecoration: 'none' }}
                            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(232,97,26,0.08)'; }}
                            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                          >
                            <div
                              className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                              style={{ background: 'rgba(232,97,26,0.1)', border: '1px solid rgba(232,97,26,0.18)' }}
                            >
                              <i className={`${svc.icon} text-[#E8611A]`} style={{ fontSize: 12 }} />
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text1)' }}>{svc.label}</span>
                          </Link>
                        ))}
                      </div>
                      <div
                        className="px-4 py-2.5 flex items-center justify-between"
                        style={{ borderTop: '1px solid var(--border)', background: 'rgba(232,97,26,0.03)' }}
                      >
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>9 hizmet kategorisi</span>
                        <Link
                          to="/hizmetler"
                          className="flex items-center gap-1 text-xs font-bold cursor-pointer"
                          style={{ color: 'var(--orange)', textDecoration: 'none' }}
                        >
                          Tümünü Gör <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={l.path}
                  to={l.path}
                  className="px-2.5 py-2 rounded-lg text-[14px] transition-all duration-250 whitespace-nowrap cursor-pointer"
                  style={{
                    fontWeight: active(l.path) ? 700 : 500,
                    background: active(l.path) ? 'rgba(232,97,26,0.18)' : 'transparent',
                    color: active(l.path)
                      ? 'var(--orange)'
                      : whiteNav ? 'rgba(255,255,255,0.90)' : 'var(--text2)',
                  }}
                  onMouseEnter={(e) => { if (!active(l.path)) e.currentTarget.style.color = whiteNav ? '#ffffff' : 'var(--text1)'; }}
                  onMouseLeave={(e) => { if (!active(l.path)) e.currentTarget.style.color = whiteNav ? 'rgba(255,255,255,0.90)' : 'var(--text2)'; }}
                >
                  {l.label}
                </Link>
              )
            ))}
          </nav>

          {/* Right side actions */}
          <div className="relative z-30 hidden lg:flex items-center gap-2 flex-shrink-0 pointer-events-auto">
            <button
              onClick={toggleTheme}
              className="theme-toggle"
              title={isDark ? 'Aydınlık Mod' : 'Karanlık Mod'}
              style={whiteNav ? { background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.25)', color: '#ffffff' } : {}}
            >
              <i className={`text-sm ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
            </button>

            <LangSwitcher whiteNav={whiteNav} />

            <Link
              to="/giris"
              className="cursor-pointer whitespace-nowrap transition-all duration-200 rounded-lg text-[13px] font-semibold"
              style={whiteNav
                ? { padding: '7px 12px', border: '1.5px solid rgba(255,255,255,0.35)', color: '#ffffff', background: 'rgba(255,255,255,0.10)' }
                : { padding: '7px 12px', border: '1.5px solid var(--border2)', color: 'var(--text1)', background: 'var(--surface)' }
              }
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.color = 'var(--orange)'; }}
              onMouseLeave={(e) => {
                if (whiteNav) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; e.currentTarget.style.color = '#ffffff'; }
                else { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text1)'; }
              }}
            >
              <i className="ri-login-box-line mr-1.5"></i>Giriş Yap
            </Link>

            <Link
              to="/uye-ol"
              className="btn-primary cursor-pointer whitespace-nowrap"
              style={{ padding: '7px 13px', fontSize: '13px' }}
            >
              <i className="ri-user-add-line mr-1.5"></i>Üye Ol
            </Link>
          </div>

          {/* Mobile: burger + theme */}
          <div className="relative z-30 lg:hidden flex items-center gap-2 flex-shrink-0 pointer-events-auto">
            <button
              onClick={toggleTheme}
              className="theme-toggle"
              title={isDark ? 'Aydınlık Mod' : 'Karanlık Mod'}
              style={whiteNav ? { background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.25)', color: '#ffffff' } : {}}
            >
              <i className={`text-sm ${isDark ? 'ri-sun-line' : 'ri-moon-line'}`}></i>
            </button>
            <LangSwitcher whiteNav={whiteNav} compact />
            <button
              onClick={() => setOpen(!open)}
              className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer"
              style={{ background: 'var(--surface)', border: '1px solid var(--border2)', color: 'var(--text1)' }}
            >
              <i className={`text-base ${open ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-400 ${open ? 'max-h-[700px]' : 'max-h-0'}`}>
        <div
          className="px-5 pb-5 pt-2"
          style={{ background: 'var(--mobile-menu-bg)', borderTop: '1px solid var(--border)' }}
        >
          {/* Phone in mobile */}
          <a
            href="tel:+908502420822"
            rel="nofollow"
            className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-[15px] mb-1 cursor-pointer transition-colors duration-200"
            style={{ color: 'var(--text1)', fontWeight: 600 }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--orange)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text1)'; }}
          >
            <i className="ri-phone-line text-xl flex-shrink-0 text-[#E8611A]" aria-hidden />
            <span className="flex flex-col gap-0.5 min-w-0 leading-snug">
              <span className="font-semibold">Müşteri Hizmetleri</span>
              <span className="text-[15px] font-bold tracking-wide" style={{ color: 'inherit' }}>
                0850 242 0 822
              </span>
            </span>
          </a>

          {links.map((l) => (
            l.hasDropdown ? (
              <div key={l.path}>
                <button
                  onClick={() => setMobileServicesOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm my-1 cursor-pointer transition-all duration-300"
                  style={{
                    fontWeight: active(l.path) ? 700 : 500,
                    background: active(l.path) ? 'rgba(232,97,26,0.15)' : 'transparent',
                    color: active(l.path) ? 'var(--orange)' : 'var(--text2)',
                  }}
                >
                  {l.label}
                  <i className={`ri-arrow-down-s-line transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileServicesOpen && (
                  <div className="ml-4 mb-1 space-y-0.5">
                    {serviceLinks.map((svc, i) => (
                      <Link
                        key={i}
                        to={svc.path}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs cursor-pointer"
                        style={{ color: 'var(--text2)', textDecoration: 'none', fontWeight: 500 }}
                      >
                        <i className={`${svc.icon} text-[#E8611A]`} style={{ fontSize: 12 }} />
                        {svc.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={l.path}
                to={l.path}
                className="flex items-center px-4 py-3 rounded-xl text-sm my-1 cursor-pointer transition-all duration-300"
                style={{
                  fontWeight: active(l.path) ? 700 : 500,
                  background: active(l.path) ? 'rgba(232,97,26,0.15)' : 'transparent',
                  color: active(l.path) ? 'var(--orange)' : 'var(--text2)',
                }}
              >
                {l.label}
              </Link>
            )
          ))}

          <div className="flex gap-2 mt-3">
            <Link
              to="/giris"
              className="flex-1 text-center py-3 rounded-xl text-sm cursor-pointer font-semibold"
              style={{ background: 'var(--surface)', border: '1.5px solid var(--border2)', color: 'var(--text1)' }}
            >
              <i className="ri-login-box-line mr-1.5"></i>Giriş Yap
            </Link>
            <Link
              to="/uye-ol"
              className="flex-1 text-center py-3 rounded-xl text-sm cursor-pointer font-semibold"
              style={{ background: 'var(--orange)', color: '#fff' }}
            >
              <i className="ri-user-add-line mr-1.5"></i>Üye Ol
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
